// Dialog (job page round): the two dialogs every place uses, on the dialog layer (Layer).
// - ConfirmDialog: one decision that has to be made before anything else ("Complete the job", "Cancel the
//   job"). What happens, in a sentence; the action named the same on its button; optionally a reason the
//   person must give. Its button is ink, never red: cancelling a job is a normal day's work, and red is
//   for a problem (DESIGN.md §2).
// - FormDialog: a few fields to change and save ("Edit details", "Move the visit"). Saving keeps the words
//   and the button dashed (waiting); a save that fails says so in red and keeps every change; closing with
//   unsaved changes asks first, in its own footer, never a second dialog.
// Both: the first line is the title beside Close; the buttons sit under a hairline at the bottom, the
// action first. Layer keeps focus inside, Escape and Close close it, and focus goes back to what opened
// it. While it's working, nothing closes it. Reads only --dlg-*; Layer, Button, Link and Field read
// their own.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';
import { TextField } from '../Field/Field.tsx';
import { Layer, type DialogSize } from '../Layer/Layer.tsx';
import './Dialog.css';

/** What a failed save or action says: the error's own words when it has them. */
function failLine(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/** Waits a frame, then focuses the first field that says it's wrong (or the first field). */
function focusFirst(root: HTMLElement | null, invalidOnly: boolean) {
  requestAnimationFrame(() => {
    const sel = invalidOnly ? '[aria-invalid="true"]' : 'input, textarea, select';
    root?.querySelector<HTMLElement>(sel)?.focus();
  });
}

interface FrameProps {
  title: string;
  children?: ReactNode;
  /** A failure, in red under the body, read out at once. */
  error?: string | null;
  footer: ReactNode;
}

/** The inside of every dialog: title, body, what went wrong, the buttons. */
export function DialogFrame({ title, children, error, footer }: FrameProps) {
  return (
    <div className="fl-dlg">
      <h2 className="fl-dlg__title">{title}</h2>
      {children ? <div className="fl-dlg__body">{children}</div> : null}
      {error ? (
        <p className="fl-dlg__error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="fl-dlg__foot">{footer}</div>
    </div>
  );
}

export interface ConfirmReason {
  /** The field's label ("Why is it cancelled?"). */
  label: string;
  /** Under it ("Jordan sees this in the text we send."). */
  description?: string;
  placeholder?: string;
  /** What it says when it's left empty. */
  missing: string;
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Its name for screen readers; the title unless it says. */
  label?: string;
  /** The question, as the action ("Cancel job 24612?"). */
  title: string;
  /** What happens, in a sentence or two. */
  children?: ReactNode;
  /** The action, named as on the button that opened it ("Cancel the job"). */
  confirmLabel: string;
  /** Leaving it as it is ("Keep the job"). */
  cancelLabel?: string;
  /** Asks for a reason first, required. */
  reason?: ConfirmReason;
  /** Does it; a rejection keeps the dialog open and says what went wrong. Closes on success. */
  onConfirm: (reason: string) => Promise<void> | void;
  /** What a failure says when the error has no words of its own. */
  failText?: string;
  /** Storybook only: opens already failed with this line. */
  previewError?: string;
  /** Focus starts on the cancel button, for a step that can't be undone (Prepare and lock). */
  startOnCancel?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  label,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Not now',
  reason,
  onConfirm,
  failText = 'That didn’t go through. Nothing has changed; try again.',
  previewError,
  startOnCancel = false,
}: ConfirmDialogProps) {
  const [why, setWhy] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(previewError ?? null);
  const body = useRef<HTMLFormElement>(null);

  // closing (or opening again) starts it fresh
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setWhy('');
      setTried(false);
      setError(previewError ?? null);
    }
  }

  // after the layer has put the focus on its first button: the cancel button, the footer's last
  useEffect(() => {
    if (!isOpen || !startOnCancel) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        const buttons = body.current?.querySelectorAll<HTMLButtonElement>('button');
        buttons?.[buttons.length - 1]?.focus();
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [isOpen, startOnCancel]);

  const missing = reason && !why.trim() ? reason.missing : null;
  const close = () => {
    if (!busy) onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setTried(true);
    if (missing) {
      focusFirst(body.current, true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(why.trim());
      setBusy(false);
      onClose();
    } catch (err) {
      setBusy(false);
      setError(failLine(err, failText));
    }
  };

  return (
    <Layer
      level="dialog"
      size="sm"
      label={label ?? title}
      isOpen={isOpen}
      onClose={close}
      isDismissable={!busy}
    >
      <form ref={body} onSubmit={(e) => void submit(e)} noValidate>
        <DialogFrame
          title={title}
          error={error}
          footer={
            <>
              <Button variant="primary" type="submit" isBusy={busy}>
                {confirmLabel}
              </Button>
              <Button onPress={close} isDisabled={busy}>
                {cancelLabel}
              </Button>
            </>
          }
        >
          {children ? <div className="fl-dlg__text">{children}</div> : null}
          {reason ? (
            <TextField
              label={reason.label}
              description={reason.description}
              placeholder={reason.placeholder}
              isRequired
              multiline
              autoFocus
              value={why}
              onChange={setWhy}
              isDisabled={busy}
              error={tried ? missing : null}
            />
          ) : null}
        </DialogFrame>
      </form>
    </Layer>
  );
}

export interface FormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  label?: string;
  /** The thing being changed ("Edit details"). */
  title: string;
  /** The fields: Field's TextField and SelectField, a ChoiceGroup. */
  children: ReactNode;
  /** "Save changes", "Move the visit". */
  submitLabel?: string;
  cancelLabel?: string;
  /** Whether anything differs from what was opened: closing then asks first, and the scrim won't close it. */
  isDirty: boolean;
  /**
   * Checked before saving: false when a field is wrong. The fields then say why, and focus goes to the
   * first that does.
   */
  validate?: () => boolean;
  /** Saves; throw to say it didn't save (the words stay). Closes on success. */
  onSubmit: () => Promise<void> | void;
  /** md a few fields; lg two columns of them. */
  size?: Extract<DialogSize, 'md' | 'lg'>;
  failText?: string;
  /** Storybook only: draws it failed, or asking about unsaved changes. */
  previewState?: 'failed' | 'discard';
  /**
   * One sentence above the buttons: exactly what Save writes, or why it's off (the timesheet's quick
   * edit, timesheet rules §3). Read out when it changes.
   */
  note?: ReactNode;
  /** A link after the buttons ("Timesheet ›"); closing by it asks first like Cancel, so the page decides. */
  extra?: ReactNode;
  /** The question closing with changes asks: "Discard your change to Fri Oct 23?" */
  discardText?: string;
  /** Its button ("Discard"). */
  discardLabel?: string;
  /** Save is off (muted words in a solid frame) until there's something valid to save; note says why. */
  isSubmitDisabled?: boolean;
}

export function FormDialog({
  isOpen,
  onClose,
  label,
  title,
  children,
  submitLabel = 'Save changes',
  cancelLabel = 'Cancel',
  isDirty,
  validate,
  onSubmit,
  size = 'md',
  failText = 'Your changes didn’t save. They’re still here; try again.',
  previewState,
  note,
  extra,
  discardText = 'Discard your changes?',
  discardLabel = 'Discard them',
  isSubmitDisabled,
}: FormDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(previewState === 'failed' ? failText : null);
  const [asking, setAsking] = useState(previewState === 'discard');
  const body = useRef<HTMLFormElement>(null);

  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setError(previewState === 'failed' ? failText : null);
      setAsking(previewState === 'discard');
    }
  }

  // Close, Escape or Cancel: with unsaved changes it asks first; while saving, nothing closes it
  const close = () => {
    if (busy) return;
    if (isDirty && !asking) {
      setAsking(true);
      return;
    }
    onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || isSubmitDisabled) return;
    setAsking(false);
    if (validate && !validate()) {
      focusFirst(body.current, true);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit();
      setBusy(false);
      onClose();
    } catch (err) {
      setBusy(false);
      setError(failLine(err, failText));
    }
  };

  const footer = asking ? (
    <>
      <p className="fl-dlg__ask" role="alert">
        {discardText}
      </p>
      <Button variant="primary" onPress={onClose}>
        {discardLabel}
      </Button>
      <Button onPress={() => setAsking(false)}>Keep editing</Button>
    </>
  ) : (
    <>
      {note ? (
        <p className="fl-dlg__note" aria-live="polite">
          {note}
        </p>
      ) : null}
      <Button variant="primary" type="submit" isBusy={busy} isDisabled={!busy && isSubmitDisabled}>
        {submitLabel}
      </Button>
      <Button onPress={close} isDisabled={busy}>
        {cancelLabel}
      </Button>
      {extra ? <span className="fl-dlg__extra">{extra}</span> : null}
    </>
  );

  return (
    <Layer
      level="dialog"
      size={size}
      label={label ?? title}
      isOpen={isOpen}
      onClose={close}
      isDismissable={!busy && !isDirty}
    >
      <form ref={body} onSubmit={(e) => void submit(e)} noValidate>
        <DialogFrame title={title} error={error} footer={footer}>
          <fieldset className="fl-dlg__fields" data-size={size} disabled={busy}>
            {children}
          </fieldset>
        </DialogFrame>
      </form>
    </Layer>
  );
}
